/**
 * The outbound turn-frame queue's own logic, kept out of the socket hook.
 *
 * Everything here is parameter-driven — nothing reads a ref, React state, or
 * the socket — which is what makes the batching and dedup rules testable
 * without a rendered hook. `useAssistantTurnLifecycle` owns the queue itself
 * and calls in.
 */

/** One entry in the outbound turn-frame queue — see the lifecycle hook's `pendingFramesRef`. */
export type PendingUserMessage = {
  kind: 'user';
  content: string;
  clientMessageId: string;
  attachmentDocumentIds?: string[];
};

export type PendingTurnFrame = PendingUserMessage | { kind: 'alert-expand'; alertId: string };

/**
 * Whether an expand for `alertId` has already been asked for and not yet
 * resolved — either it is the turn currently running, or it is sitting in the
 * queue waiting to run.
 *
 * The strip's own guard asks a different question ("does the transcript already
 * have a card for this alert"), which is the right one once a turn has landed
 * but is still false during the window between the click and the card. Two fast
 * clicks both pass it, and the second becomes a separately billed turn for an
 * alert already being expanded.
 *
 * Deliberately not a "has this alert ever been expanded" check: an expand that
 * fails leaves no card, and the user must be able to click again.
 *
 * @param alertId - the alert the click names.
 * @param inFlightExpandId - the alert the running turn is expanding, or `null`.
 * @param queue - the outbound turn-frame queue, searched for a pending expand.
 * @returns `true` when the click should be dropped as a duplicate.
 */
export function isExpandAlreadyPending(
  alertId: string,
  inFlightExpandId: string | null,
  queue: readonly PendingTurnFrame[]
): boolean {
  return (
    inFlightExpandId === alertId || queue.some((entry) => entry.kind === 'alert-expand' && entry.alertId === alertId)
  );
}

/**
 * The leading run of the queue that can go out as ONE turn, plus what's left.
 *
 * Consecutive user messages batch into a single `user_message` frame (that
 * batching is what makes mid-turn typing read as one turn). An alert expand is
 * its own turn, so it never joins a batch and never takes more than itself.
 * The remainder stays queued and drains on the next `turn_complete`.
 *
 * @param queue - the pending frames, oldest first.
 * @returns `turn` — what goes out now; `rest` — what stays queued.
 */
export function takeNextTurn(queue: PendingTurnFrame[]): { turn: PendingTurnFrame[]; rest: PendingTurnFrame[] } {
  if (queue[0]?.kind === 'alert-expand') {
    return { turn: [queue[0]], rest: queue.slice(1) };
  }
  const end = queue.findIndex((entry) => entry.kind !== 'user');
  const cut = end === -1 ? queue.length : end;
  return { turn: queue.slice(0, cut), rest: queue.slice(cut) };
}

/**
 * Strip the queue's own discriminator — the wire shape carries no `kind`.
 *
 * @param entry - a queued user message.
 * @returns the `user_message` part, with `attachmentDocumentIds` omitted rather
 * than sent as `undefined` when the message carried none.
 */
export function toUserMessagePart(entry: PendingUserMessage): {
  content: string;
  clientMessageId: string;
  attachmentDocumentIds?: string[];
} {
  return {
    content: entry.content,
    clientMessageId: entry.clientMessageId,
    ...(entry.attachmentDocumentIds ? { attachmentDocumentIds: entry.attachmentDocumentIds } : {}),
  };
}
